import React from "react";
import { Check } from "lucide-react";
import { cn } from "../../lib/utils";

export interface MobileOption {
  value: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
}

interface MobileOptionListProps {
  options: MobileOption[];
  value: string;
  onChange: (value: string) => void;
}

/** Single-choice list with large touch targets, used in place of radio card grids on phones. */
const MobileOptionList: React.FC<MobileOptionListProps> = ({
  options,
  value,
  onChange,
}) => (
  <div
    role="radiogroup"
    className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-100"
  >
    {options.map((option) => {
      const selected = option.value === value;
      return (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={selected}
          onClick={() => onChange(option.value)}
          className={cn(
            "flex w-full items-center gap-3 border-0 px-3.5 py-3 text-left transition-colors [-webkit-tap-highlight-color:transparent]",
            selected ? "bg-primary-50" : "bg-white active:bg-gray-50",
          )}
        >
          {option.icon && (
            <span
              className={cn(
                "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
                selected
                  ? "bg-white text-primary-600"
                  : "bg-gray-50 text-gray-600",
              )}
            >
              {option.icon}
            </span>
          )}
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-gray-900">
              {option.label}
            </span>
            {option.description && (
              <span className="block truncate text-xs text-gray-500">
                {option.description}
              </span>
            )}
          </span>
          <span
            className={cn(
              "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2",
              selected
                ? "border-primary-600 bg-primary-600 text-white"
                : "border-gray-300",
            )}
          >
            {selected && <Check className="h-3 w-3" strokeWidth={3} />}
          </span>
        </button>
      );
    })}
  </div>
);

export default MobileOptionList;
